import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { consumePostAuthDestination } from '../utils/postAuth';
import { Icon } from '../tokens';
import TrolyLockup from '../components/TrolyLockup';
import './Auth.css';

const FB_APP_ID = process.env.REACT_APP_FB_APP_ID || '';
const FB_SDK_VERSION = process.env.REACT_APP_FB_SDK_VERSION || 'v19.0';
const FB_BIND_KEY = 'troly_fb_bind';

// Devuelve `window.FB` ya inicializado. El SDK normalmente ya se precargó
// vía el snippet en public/index.html; este loader cubre dos casos:
//   1. SDK listo  → resuelve de inmediato.
//   2. Script en vuelo (carrera por clic temprano) → hace polling corto
//      hasta que `window.FB` quede disponible.
//   3. Script ausente (snippet desactivado) → lo inyecta a demanda.
function loadFacebookSdk() {
  return new Promise((resolve, reject) => {
    if (!FB_APP_ID) return reject(new Error('FB_NOT_CONFIGURED'));
    if (typeof window === 'undefined') return reject(new Error('NO_WINDOW'));
    if (window.FB) return resolve(window.FB);

    const ensureInit = () => {
      try { window.FB.init({ appId: FB_APP_ID, cookie: true, xfbml: false, version: FB_SDK_VERSION }); } catch {}
      resolve(window.FB);
    };

    const id = 'facebook-jssdk';
    const existing = document.getElementById(id);
    if (existing) {
      const start = Date.now();
      const tick = () => {
        if (window.FB) return ensureInit();
        if (Date.now() - start > 10000) return reject(new Error('FB_SDK_LOAD_FAILED'));
        setTimeout(tick, 50);
      };
      return tick();
    }

    window.fbAsyncInit = ensureInit;
    const js = document.createElement('script');
    js.id = id;
    js.async = true;
    js.defer = true;
    js.crossOrigin = 'anonymous';
    js.src = 'https://connect.facebook.net/en_US/sdk.js';
    js.onerror = () => reject(new Error('FB_SDK_LOAD_FAILED'));
    document.body.appendChild(js);
  });
}

// SVG oficial del logo de Facebook (color #1877F2 sobre fondo blanco redondo).
function FacebookGlyph({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 36 36" aria-hidden="true">
      <circle cx="18" cy="18" r="18" fill="#1877F2" />
      <path
        fill="#fff"
        d="M22.7 19.1l.7-4.6h-4.4v-3c0-1.3.6-2.5 2.6-2.5h2V4.9s-1.8-.3-3.6-.3c-3.7 0-6.1 2.2-6.1 6.3v3.6h-4v4.6h4V30h5v-10.9h3.8z"
      />
    </svg>
  );
}

/* ── Shared dark shell ─────────────────────────────────────── */
function Shell({ children }) {
  const { t } = useTranslation();
  return (
    <div className="auth-shell">
      <div className="auth-shell__content">
        {children}
      </div>
      <p className="auth-copy">{t('auth_shell.copyright')}</p>
    </div>
  );
}

function TrolyLogo({ size = 32 }) {
  return <TrolyLockup inverse size={size} />;
}

/* ── 02 · Trigger (gate) ───────────────────────────────────── */
export function Trigger() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { facebookLogin } = useAuth();
  const [fbLoading, setFbLoading] = useState(false);
  const [fbError, setFbError] = useState('');

  const handleFacebook = async () => {
    setFbError('');
    if (!FB_APP_ID) { setFbError(t('trigger.fb_not_configured')); return; }
    setFbLoading(true);
    try {
      const FB = await loadFacebookSdk();
      FB.login((resp) => {
        (async () => {
          try {
            if (resp.status !== 'connected' || !resp.authResponse?.accessToken) {
              setFbError(t('trigger.fb_cancelled'));
              return;
            }
            const data = await facebookLogin(resp.authResponse.accessToken);
            if (data.status === 'logged_in') {
              navigate(consumePostAuthDestination());
              return;
            }
            if (data.status === 'invite_sent') {
              // Guardamos el bindToken para que /register lo envíe junto al
              // POST y el backend pueda vincular facebookId al nuevo usuario.
              try {
                sessionStorage.setItem(FB_BIND_KEY, JSON.stringify({
                  token: data.fbBindToken,
                  email: data.email,
                  name: data.name,
                }));
              } catch { /* sessionStorage puede fallar en modo privado */ }
              navigate('/invite', { state: { email: data.email, fromFacebook: true } });
            }
          } catch (err) {
            const code = err.response?.data?.code;
            if (code === 'EMAIL_REQUIRED') setFbError(t('trigger.fb_email_required'));
            else if (code === 'FB_NOT_CONFIGURED') setFbError(t('trigger.fb_not_configured'));
            else setFbError(err.response?.data?.message || t('trigger.fb_error'));
          } finally {
            setFbLoading(false);
          }
        })();
      }, { scope: 'email,public_profile' });
    } catch (err) {
      setFbLoading(false);
      if (err.message === 'FB_NOT_CONFIGURED') setFbError(t('trigger.fb_not_configured'));
      else setFbError(t('trigger.fb_sdk_error'));
    }
  };

  return (
    <Shell>
      <Link to="/" style={{ textDecoration: 'none' }}><TrolyLogo /></Link>

      {/* Medal */}
      <div className="medal fade-up" style={{ animationDelay: '.05s' }}>
        <div className="medal__ribbon">
          <div className="medal__rib-l" />
          <div className="medal__rib-r" />
        </div>
        <div className="medal__circle">
          <span className="medal__num">1</span>
        </div>
      </div>

      <h2 className="auth-h2 fade-up" style={{ animationDelay: '.1s' }}>
        {t('trigger.title')}
      </h2>
      <p className="auth-p fade-up" style={{ animationDelay: '.14s' }}>
        {t('trigger.desc')}
      </p>

      {fbError && <div className="error-banner fade-in" style={{ marginTop: 16 }}>{fbError}</div>}

      <div className="auth-btns fade-up" style={{ animationDelay: '.18s' }}>
        <button className="btn-teal" style={{ width: '100%' }} onClick={() => navigate('/waitlist')}>
          {t('trigger.join_waitlist')}
        </button>
        <button className="btn-outline-dark" onClick={() => navigate('/invite')}>
          {t('trigger.have_code')}
        </button>
        <button className="btn-outline-dark" onClick={() => navigate('/login')}>
          {t('trigger.have_account')}
        </button>

        {/* ── Acceso rápido vía Facebook ───────────────────── */}
        <div className="auth-divider" style={{ margin: '14px 0 6px' }}>{t('trigger.fast_track_divider')}</div>
        <p className="fb-fast-text">{t('trigger.fast_track_text')}</p>
        <button
          type="button"
          className="btn-facebook"
          onClick={handleFacebook}
          disabled={fbLoading}
          aria-label={t('trigger.fb_button')}
        >
          <FacebookGlyph />
          <span>{fbLoading ? t('trigger.fb_loading') : t('trigger.fb_button')}</span>
        </button>
      </div>
    </Shell>
  );
}

/* ── 03 · Código OTP ───────────────────────────────────────── */
export function InviteCode() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { validateCode } = useAuth();
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Email pre-cargado cuando el usuario llega desde el flujo de Facebook.
  // Se propaga a /register para fijar el correo verificado por FB.
  const prefillEmail = location.state?.email || '';
  const fromFacebook = !!location.state?.fromFacebook;

  const code = digits.join('').toUpperCase();

  const set = (i, v) => {
    const d = [...digits];
    d[i] = v.toUpperCase().slice(-1);
    setDigits(d);
    if (v && i < 5) document.getElementById(`otp-${i + 1}`)?.focus();
  };

  const handleKey = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) document.getElementById(`otp-${i - 1}`)?.focus();
  };

  const validate = async () => {
    if (code.length < 6) return setError(t('invite_code.err_incomplete'));
    setError(''); setLoading(true);
    try {
      await validateCode(code);
      navigate('/register', { state: { code, email: prefillEmail, fromFacebook } });
    } catch (err) {
      setError(err.response?.data?.message || t('invite_code.err_invalid'));
    } finally { setLoading(false); }
  };

  return (
    <Shell>
      <Link to="/" style={{ textDecoration: 'none' }}><TrolyLogo /></Link>
      <h2 className="auth-h2 fade-up" style={{ fontSize: 15, marginTop: 20 }}>{t('invite_code.title')}</h2>
      <p className="auth-p fade-up" style={{ animationDelay: '.06s' }}>
        {fromFacebook && prefillEmail
          ? t('invite_code.from_fb_hint', { email: prefillEmail })
          : <>{t('invite_code.desc_l1')}<br />{t('invite_code.desc_l2')}</>}
      </p>

      {error && <div className="error-banner fade-in">{error}</div>}

      {/* OTP grid */}
      <div className="otp-row fade-up" style={{ animationDelay: '.1s' }}>
        {digits.slice(0, 3).map((d, i) => (
          <input key={i} id={`otp-${i}`} className={`otp-cell${d ? ' otp-cell--filled' : ''}`}
            maxLength={1} value={d}
            onChange={e => set(i, e.target.value)}
            onKeyDown={e => handleKey(i, e)} />
        ))}
        <div className="otp-dash" />
        {digits.slice(3).map((d, i) => (
          <input key={i + 3} id={`otp-${i + 3}`} className={`otp-cell${d ? ' otp-cell--filled' : ''}${!d && i === 0 ? ' otp-cell--cursor' : ''}`}
            maxLength={1} value={d}
            onChange={e => set(i + 3, e.target.value)}
            onKeyDown={e => handleKey(i + 3, e)} />
        ))}
      </div>

      <div className="auth-btns fade-up" style={{ animationDelay: '.14s' }}>
        <button className="btn-teal" style={{ width: 280 }} onClick={validate} disabled={loading}>
          {loading ? t('invite_code.validating') : t('invite_code.validate')}
        </button>
        <button className="btn-outline-dark" style={{ width: 280 }}>{t('invite_code.resend')}</button>
      </div>
    </Shell>
  );
}

/* ── 04 · Waitlist (Journey A: envío inmediato del código) ──── */
export function Waitlist() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { joinWaitlist } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sentTo, setSentTo] = useState('');

  const submit = async () => {
    if (!email) return setError(t('waitlist.err_no_email'));
    setError(''); setLoading(true);
    try {
      const data = await joinWaitlist(email);
      setSentTo(data.email || email);
    } catch (err) {
      setError(err.response?.data?.message || t('waitlist.err_register'));
    } finally { setLoading(false); }
  };

  // Estado de éxito: el código ya va en camino al correo.
  if (sentTo) {
    return (
      <Shell>
        <Link to="/" style={{ textDecoration: 'none' }}><TrolyLogo /></Link>
        <div className="medal fade-up" style={{ animationDelay: '.05s' }}>
          <div className="medal__circle">
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2 11 13" /><path d="M22 2 15 22l-4-9-9-4 20-7z" /></svg>
          </div>
        </div>
        <h2 className="auth-h2 fade-up" style={{ animationDelay: '.1s' }}>Tu código está en camino</h2>
        <p className="auth-p fade-up" style={{ animationDelay: '.14s' }}>
          Lo enviamos a <strong style={{ color: '#fff' }}>{sentTo}</strong>. Revisa tu correo
          (y la carpeta de spam) y abre el enlace para completar tu registro.
        </p>
        <div className="auth-btns fade-up" style={{ animationDelay: '.18s', width: 320 }}>
          <button className="btn-teal" style={{ width: '100%' }} onClick={() => navigate('/registro')}>
            Ya tengo mi código →
          </button>
          <button className="btn-outline-dark" onClick={() => { setSentTo(''); setEmail(''); }}>
            Usar otro correo
          </button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <Link to="/" style={{ textDecoration: 'none' }}><TrolyLogo /></Link>
      <div className="badge-pill fade-up">{t('waitlist.badge')}</div>
      <h2 className="auth-h2 fade-up" style={{ animationDelay: '.06s' }}>{t('waitlist.title')}</h2>
      <p className="auth-p fade-up" style={{ animationDelay: '.1s' }}>
        {t('waitlist.desc')}
      </p>

      {error && <div className="error-banner">{error}</div>}

      <div className="auth-btns fade-up" style={{ animationDelay: '.14s', width: 320 }}>
        <input className="dark-input" type="email" placeholder={t('waitlist.email_placeholder')} value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && submit()} />
        <button className="btn-teal" style={{ width: '100%' }} onClick={submit} disabled={loading}>
          {loading ? t('waitlist.submitting') : t('waitlist.submit')}
        </button>
        <p style={{ fontSize: 11, color: '#666', textAlign: 'center', lineHeight: 1.5 }}>
          {t('waitlist.wait_label')} <span style={{ color: '#fff' }}>{t('waitlist.wait_value')}</span>
        </p>
      </div>
    </Shell>
  );
}

/* ── 05 · Waitlist Done ────────────────────────────────────── */
export function WaitlistDone() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = window.history.state?.usr;
  const position = location?.position || 1402;

  return (
    <Shell>
      <TrolyLogo />
      <div className="badge-pill fade-up">{t('waitlist.badge')}</div>
      <h2 className="auth-h2 fade-up" style={{ animationDelay: '.06s' }}>{t('waitlist_done.title')}</h2>

      <div className="queue-card fade-up" style={{ animationDelay: '.1s' }}>
        <p className="queue-card__label">{t('waitlist_done.received')}</p>
        <p className="queue-card__sub">{t('waitlist_done.you_are')}</p>
        <p className="queue-card__num">#{position?.toLocaleString()}</p>
        <p className="queue-card__sub2">{t('waitlist_done.in_queue')}</p>
        <div className="queue-card__bar">
          <div className="queue-card__fill" style={{ width: '18%' }} />
        </div>
      </div>

      <p className="auth-p fade-up" style={{ animationDelay: '.16s', marginTop: 20 }}>
        <strong style={{ color: '#fff' }}>{t('waitlist_done.skip_title')}</strong><br />
        {t('waitlist_done.skip_desc')}
      </p>

      <button className="btn-outline-dark fade-up" style={{ marginTop: 20, animationDelay: '.2s' }}
        onClick={() => navigate('/invite')}>
        {t('waitlist_done.have_code_btn')}
      </button>
    </Shell>
  );
}

/* ── 06 · Login ────────────────────────────────────────────── */
export function Login() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try { await login(email, pass); navigate(consumePostAuthDestination()); }
    catch (err) { setError(err.response?.data?.message || t('login_pg.err_invalid')); }
    finally { setLoading(false); }
  };

  return (
    <Shell>
      <TrolyLogo />
      <p style={{ fontSize: 13, color: '#a0a0a0', marginTop: 10 }} className="fade-up">{t('login_pg.welcome')}</p>

      {error && <div className="error-banner fade-in" style={{ marginTop: 16 }}>{error}</div>}

      <form onSubmit={submit} className="auth-form fade-up" style={{ animationDelay: '.12s' }}>
        <div className="dark-field">
          <label>{t('login_pg.email_label')}</label>
          <input className="dark-input" type="email" placeholder={t('login_pg.email_placeholder')} value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email" />
        </div>
        <div className="dark-field">
          <label>{t('login_pg.password_label')}</label>
          <div className="pass-wrap">
            <input className="dark-input" type={showPass ? 'text' : 'password'} placeholder="••••••••" value={pass} onChange={e => setPass(e.target.value)} required autoComplete="current-password" />
            <button type="button" className="pass-toggle" onClick={() => setShowPass(v => !v)}>
              {showPass ? Icon.eyeOff(14) : Icon.eye(14)}
            </button>
          </div>
        </div>
        <div style={{ textAlign: 'right', marginTop: 4 }}>
          <span style={{ fontSize: 11, color: 'var(--teal)', cursor: 'pointer' }}>{t('login_pg.forgot')}</span>
        </div>
        <button type="submit" className="btn-teal" style={{ width: '100%', marginTop: 8 }} disabled={loading}>
          {loading ? t('login_pg.submitting') : t('login_pg.submit')}
        </button>
        <p style={{ textAlign: 'center', fontSize: 11, color: '#888', marginTop: 10 }}>
          {t('login_pg.no_account')}{' '}
          <span style={{ color: 'var(--teal)', fontWeight: 500, cursor: 'pointer' }} onClick={() => navigate('/invite')}>
            {t('login_pg.register')}
          </span>
        </p>
      </form>
    </Shell>
  );
}

/* ── 07 · Registro (Journey A) ─────────────────────────────── */
// Página /registro: código (pre-llenado desde ?code=), nombre, email y
// contraseña. Valida el código en vivo (check verde) contra
// POST /api/auth/validate-code. Al registrar, el plan Troly Pro queda activo
// (gratis, para siempre — ya no existe el trial de 14 días).
export function Registro() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { register, validateInviteCode, startTrial } = useAuth();

  const initialCode = (new URLSearchParams(window.location.search).get('code') || '').toUpperCase();
  const [form, setForm] = useState({ code: initialCode, name: '', email: '', password: '', city: '', country: '' });
  const [codeStatus, setCodeStatus] = useState('idle'); // idle | checking | valid | invalid
  const [codeMsg, setCodeMsg] = useState('');
  // Dirección a la que se envió la invitación. La rellenamos abajo para que
  // nadie cree la cuenta con otro correo por un dedazo y luego no reciba nada.
  const [invitedEmail, setInvitedEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Valida el código en vivo (debounce 500ms) cuando tiene longitud suficiente.
  useEffect(() => {
    const code = form.code.trim().toUpperCase();
    if (code.length < 4) { setCodeStatus('idle'); setCodeMsg(''); return; }
    let cancelled = false;
    setCodeStatus('checking'); setCodeMsg('');
    const id = setTimeout(async () => {
      try {
        const data = await validateInviteCode(code);
        if (!cancelled) {
          setCodeStatus('valid'); setCodeMsg('');
          setInvitedEmail(data?.email || '');
          // Solo rellenamos si el campo sigue vacío: si ya escribió algo,
          // es suyo y no se lo pisamos.
          if (data?.email) setForm((f) => (f.email.trim() ? f : { ...f, email: data.email }));
        }
      } catch (err) {
        if (!cancelled) {
          setCodeStatus('invalid');
          setCodeMsg(err.response?.data?.message || 'Este código no existe o ya fue usado.');
          setInvitedEmail('');
        }
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(id); };
  }, [form.code, validateInviteCode]);

  const submit = async (e) => {
    e.preventDefault();
    if (codeStatus === 'invalid') return setError('El código no es válido.');
    if (!form.city.trim() || !form.country.trim()) return setError(t('register_pg.err_loc_required'));
    setError(''); setLoading(true);
    // La ubicación define la elegibilidad territorial del sorteo (growth).
    const location = `${form.city.trim()}, ${form.country.trim()}`;
    try {
      await register(form.name, form.email, form.password, form.code.trim().toUpperCase(), location);
      try { await startTrial(); } catch { /* el trial se puede iniciar luego en /profile */ }
      navigate(consumePostAuthDestination());
    } catch (err) {
      setError(err.response?.data?.message || 'No pudimos completar tu registro.');
    } finally { setLoading(false); }
  };

  return (
    <Shell>
      <Link to="/" style={{ textDecoration: 'none' }}><TrolyLogo /></Link>
      <h2 className="auth-h2 fade-up" style={{ fontSize: 20, marginTop: 16 }}>Completa tu registro</h2>
      <p className="auth-p fade-up" style={{ animationDelay: '.06s' }}>
        {t('register_pg.subtitle')}
      </p>

      {error && <div className="error-banner fade-in">{error}</div>}

      <form onSubmit={submit} className="auth-form fade-up" style={{ animationDelay: '.08s' }}>
        <div className="dark-field">
          <label>Código de invitación</label>
          <div style={{ position: 'relative' }}>
            <input
              className="dark-input"
              type="text"
              placeholder="TROLY-XXXX"
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
              style={{ textTransform: 'uppercase', letterSpacing: '.1em', paddingRight: 38 }}
              required
            />
            {codeStatus === 'valid' && (
              <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: '#14b8a6', display: 'flex' }} aria-label="Código válido">
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><path d="M4 10.5l3.5 3.5L16 5.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </span>
            )}
            {codeStatus === 'checking' && (
              <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: '#888', fontSize: 11 }}>…</span>
            )}
          </div>
          {codeStatus === 'invalid' && (
            <span style={{ fontSize: 11, color: '#f87171', marginTop: 4, display: 'block' }}>
              {codeMsg}{' '}
              <Link to="/waitlist" style={{ color: 'var(--teal)' }}>¿No tienes uno? Únete a la lista.</Link>
            </span>
          )}
        </div>
        <div className="dark-field">
          <label>Nombre</label>
          <input className="dark-input" type="text" placeholder="Tu nombre" value={form.name} onChange={set('name')} required autoComplete="name" />
        </div>
        <div className="dark-field">
          <label>Email</label>
          {/* Cuando la invitación trae correo, el campo queda bloqueado: la
              cuenta tiene que quedar en la dirección que recibió la invitación.
              readOnly y no disabled — así se puede seleccionar y copiar, y los
              lectores de pantalla lo anuncian en vez de saltárselo. */}
          <input
            className="dark-input"
            type="email"
            placeholder="email@ejemplo.com"
            value={form.email}
            onChange={set('email')}
            required
            autoComplete="email"
            readOnly={Boolean(invitedEmail)}
            aria-readonly={Boolean(invitedEmail)}
            title={invitedEmail ? 'Este es el correo al que se envió tu invitación' : undefined}
            style={invitedEmail ? { opacity: 0.75, cursor: 'not-allowed' } : undefined}
          />
          {invitedEmail && (
            <span style={{ fontSize: 11, color: '#888', marginTop: 4, display: 'block' }}>
              Tu cuenta queda en el correo que recibió la invitación.{' '}
              <Link to="/waitlist" style={{ color: 'var(--teal)' }}>¿Necesitas otro?</Link>
            </span>
          )}
        </div>
        <div className="dark-field">
          <label>Contraseña</label>
          <input className="dark-input" type="password" placeholder="••••••••" value={form.password} onChange={set('password')} required autoComplete="new-password" minLength={6} />
        </div>
        <div className="dark-field">
          <label>{t('register_pg.city_label')}</label>
          <input className="dark-input" type="text" placeholder={t('register_pg.city_placeholder')} value={form.city} onChange={set('city')} required autoComplete="address-level2" />
        </div>
        <div className="dark-field">
          <label>{t('register_pg.country_label')}</label>
          <input className="dark-input" type="text" placeholder={t('register_pg.country_placeholder')} value={form.country} onChange={set('country')} required autoComplete="country-name" />
        </div>
        <button type="submit" className="btn-teal" style={{ width: '100%', marginTop: 4 }} disabled={loading || codeStatus === 'invalid'}>
          {loading ? 'Creando cuenta…' : 'Crear mi cuenta →'}
        </button>
        <p style={{ textAlign: 'center', fontSize: 11, color: '#888', marginTop: 10 }}>
          ¿Ya tienes cuenta? <Link to="/login" style={{ color: 'var(--teal)', fontWeight: 500 }}>Inicia sesión</Link>
        </p>
      </form>
    </Shell>
  );
}

/* ── Register (after valid code) ───────────────────────────── */
export function Register() {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { t } = useTranslation();
  const { register } = useAuth();
  // Get code passed via navigation state
  const code = new URLSearchParams(window.location.search).get('code') ||
    routerLocation.state?.code ||
    (typeof window !== 'undefined' && window.history.state?.usr?.code) || '';

  // Si el usuario viene del flujo de Facebook, el correo viene en state y se
  // pre-llena bloqueado. El fbBindToken se recupera de sessionStorage para
  // que el backend asocie facebookId al crear el usuario.
  const fbPrefillEmail = routerLocation.state?.email || '';
  const fromFacebook = !!routerLocation.state?.fromFacebook;
  let fbBindToken = '';
  let fbPrefillName = '';
  if (fromFacebook) {
    try {
      const raw = sessionStorage.getItem(FB_BIND_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        fbBindToken = parsed.token || '';
        fbPrefillName = parsed.name || '';
      }
    } catch { /* ignore */ }
  }

  const [form, setForm] = useState({
    name: fbPrefillName,
    email: fbPrefillEmail,
    password: '',
    confirm: '',
    code,
    city: '',
    country: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) return setError(t('register_pg.err_pass_mismatch'));
    if (!form.city.trim() || !form.country.trim()) return setError(t('register_pg.err_loc_required'));
    setError(''); setLoading(true);
    const location = `${form.city.trim()}, ${form.country.trim()}`;
    try {
      await register(form.name, form.email, form.password, form.code, location, fbBindToken || undefined);
      if (fromFacebook) { try { sessionStorage.removeItem(FB_BIND_KEY); } catch {} }
      navigate(consumePostAuthDestination());
    }
    catch (err) { setError(err.response?.data?.message || t('register_pg.err_register')); }
    finally { setLoading(false); }
  };

  return (
    <Shell>
      <TrolyLogo />
      <h2 className="auth-h2 fade-up" style={{ fontSize: 20, marginTop: 16 }}>{t('register_pg.title')}</h2>

      {error && <div className="error-banner fade-in">{error}</div>}

      <form onSubmit={submit} className="auth-form fade-up" style={{ animationDelay: '.08s' }}>
        <div className="dark-field">
          <label>{t('register_pg.name_label')}</label>
          <input className="dark-input" type="text" placeholder={t('register_pg.name_placeholder')} value={form.name} onChange={set('name')} required />
        </div>
        <div className="dark-field">
          <label>{t('register_pg.email_label')}</label>
          <input
            className="dark-input"
            type="email"
            placeholder={t('register_pg.email_placeholder')}
            value={form.email}
            onChange={set('email')}
            readOnly={fromFacebook}
            disabled={fromFacebook}
            required
          />
          {fromFacebook && (
            <span style={{ fontSize: 10, color: 'var(--teal)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
              <FacebookGlyph size={12} /> {t('register_pg.email_locked_hint')}
            </span>
          )}
        </div>
        <div className="dark-field">
          <label>{t('register_pg.password_label')}</label>
          <input className="dark-input" type="password" placeholder="••••••••" value={form.password} onChange={set('password')} required />
        </div>
        <div className="dark-field">
          <label>{t('register_pg.confirm_label')}</label>
          <input className="dark-input" type="password" placeholder="••••••••" value={form.confirm} onChange={set('confirm')} required />
        </div>
        <div className="dark-field">
          <label>{t('register_pg.city_label')}</label>
          <input className="dark-input" type="text" placeholder={t('register_pg.city_placeholder')} value={form.city} onChange={set('city')} required />
        </div>
        <div className="dark-field">
          <label>{t('register_pg.country_label')}</label>
          <input className="dark-input" type="text" placeholder={t('register_pg.country_placeholder')} value={form.country} onChange={set('country')} required />
        </div>
        <div className="dark-field">
          <label>{t('register_pg.code_label')}</label>
          <input className="dark-input" type="text" placeholder={t('register_pg.code_placeholder')} value={form.code} onChange={set('code')} style={{ textTransform: 'uppercase', letterSpacing: '.1em' }} required />
        </div>
        <button type="submit" className="btn-teal" style={{ width: '100%', marginTop: 4 }} disabled={loading}>
          {loading ? t('register_pg.submitting') : t('register_pg.submit')}
        </button>
        <p style={{ textAlign: 'center', fontSize: 11, color: '#888', marginTop: 10 }}>
          {t('register_pg.have_account')}{' '}
          <Link to="/login" style={{ color: 'var(--teal)', fontWeight: 500 }}>{t('register_pg.login_link')}</Link>
        </p>
      </form>
    </Shell>
  );
}
